import {
  AppCard,
  Button,
  Icon,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiPencil } from "@mdi/js";
import { Box, CircularProgress, Typography } from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DuimpRequestDetailsType } from "../../../../../@types/services/DuimpRequest";
import { DuimpRequestDetails } from "../../../../../components/molecules/DuimpRequestForm/details";
import { MmHistoryLogs } from "../../../../../components/molecules/MmHistoryLogs";
import { MmRequestAttachments } from "../../../../../components/molecules/MmRequestAttachments";
import { MmRequestHeader } from "../../../../../components/molecules/MmRequestHeader";
import { MmRequestComments } from "../../../../../components/molecules/MmRequestsComments";
import {
  MmRequestTabs,
  type MmRequestTabValue,
} from "../../../../../components/molecules/MmRequestTabs";
import { useAuth } from "../../../../../hooks/useAuth";
import { fetchDuimpRequest } from "../../../../../services/duimpRequests";
import { mmRequestTabsSearchSchema } from "../../mm_requests/-schemas/mmRequestTabsSearchSchema";

const ShowDuimpRequest: React.FC = () => {
  const { t } = useTranslation();
  const navigate = Route.useNavigate();
  const { user } = useAuth();

  const { duimpRequestId } = Route.useParams();
  const { tab } = Route.useSearch();

  const [isFetchingData, setIsFetchingData] = useState<boolean>(true);
  const [mmRequestId, setMmRequestId] = useState<number | null>(null);
  const [requestDetailsData, setRequestDetailsData] =
    useState<DuimpRequestDetailsType>();

  useEffect(() => {
    if (!duimpRequestId) return;

    fetchDuimpRequest(duimpRequestId)
      .then((response) => {
        setMmRequestId(response.mm_request.id);

        setRequestDetailsData({
          id: duimpRequestId,
          mm_request: {
            assigned_to: response.mm_request.assigned_to,
            completed: response.mm_request.process_instance.completed,
            created_at: response.mm_request.created_at,
            deadline: response.mm_request.deadline,
            description: response.mm_request.description,
            id: response.mm_request.id,
            matnumber: response.mm_request.matnumber,
            quality_deviation: response.mm_request.quality_deviation,
            quality_deviation_description:
              response.mm_request.quality_deviation_description,
            quantity_of_materials: response.mm_request.quantity_of_materials,
            requester_area: response.mm_request.requester_area,
            requester_field: response.mm_request.requester_field,
            subject: response.mm_request.subject,
            threat: response.mm_request.threat,
            updated_at: response.mm_request.updated_at,
          },
        });
      })
      .catch(() => {
        enqueueSnackbar(t("mmRequests.fetchError"), {
          variant: "error",
        });
      })
      .finally(() => {
        setIsFetchingData(false);
      });
  }, [duimpRequestId, t]);

  const handleTabChange = useCallback(
    (newValue: MmRequestTabValue) => {
      navigate({
        replace: true,
        resetScroll: false,
        search: (prev) => ({
          ...prev,
          tab: newValue,
        }),
      });
    },
    [navigate],
  );

  const handleEdit = useCallback((): void => {
    if (!duimpRequestId) return;

    navigate({
      params: { duimpRequestId },
      to: "/vbuyer/duimp_requests/$duimpRequestId/edit",
    });
  }, [duimpRequestId, navigate]);

  const userCanEdit = useMemo(() => {
    const mmRequest = requestDetailsData?.mm_request;

    if (!user || !mmRequest) return false;

    return (
      (user.roles_names.includes("Edit material requests") ||
        user.id === mmRequest.assigned_to?.id) &&
      !mmRequest.completed
    );
  }, [requestDetailsData?.mm_request, user]);

  const title = mmRequestId
    ? `${t("common.duimpRequest")} #${mmRequestId}`
    : t("common.duimpRequest");

  if (isFetchingData) {
    return (
      <PageContainer showBackButton title={title}>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  const isCompleted = requestDetailsData?.mm_request.completed ?? false;

  return (
    <PageContainer showBackButton title={title}>
      {!!mmRequestId && <MmRequestHeader id={mmRequestId} />}

      <AppCard>
        <MmRequestTabs onChange={handleTabChange} value={tab} />
        <Box sx={{ marginTop: "32px" }}>
          {tab === "details" && requestDetailsData && (
            <AppCard
              header={
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="h6">{t("details")}</Typography>
                  {userCanEdit && (
                    <Button
                      onClick={handleEdit}
                      startIcon={<Icon icon={mdiPencil} />}
                      variant="outlined"
                    >
                      {t("common.edit")}
                    </Button>
                  )}
                </Box>
              }
            >
              <DuimpRequestDetails requestDetailsData={requestDetailsData} />
            </AppCard>
          )}
          {tab === "attachments" && mmRequestId && (
            <MmRequestAttachments
              isCompleted={isCompleted}
              mmRequestId={mmRequestId}
            />
          )}
          {tab === "comments" && mmRequestId && (
            <MmRequestComments
              isCompleted={isCompleted}
              mmRequestId={mmRequestId}
            />
          )}
          {tab === "history" && mmRequestId && (
            <MmHistoryLogs mmRequestId={mmRequestId} />
          )}
        </Box>
      </AppCard>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/duimp_requests/$duimpRequestId/",
)({
  component: ShowDuimpRequest,
  validateSearch: mmRequestTabsSearchSchema,
});
